import { SECOND } from '../../utils/time';
import { isWaveOfType } from '../../wave/utils/wave.util';
import { Wave } from '../../wave/types/wave.type';
import { getWaveMinMaxTimeValues } from './min-max-time-value.utils';
import { Leads } from '../../ecg/types/signal';

type TimeAmplitude = { time: number; amplitude: number };

export const computeNetDeflection = (
  wave: Wave,
  signal: number[],
  frequency: number,
) => {
  const onsetIndex = ms2ind(wave.onset, frequency);
  const onsetAmplitude = signal[onsetIndex];
  const onsetTimeValue = { time: wave.onset, amplitude: onsetAmplitude };
  const [minTimeValue, maxTimeValue] = getWaveMinMaxTimeValues(
    wave,
    signal,
    frequency,
  );

  if (isWaveOfType(wave, 'P')) {
    const offsetIndex = ms2ind(wave.offset, frequency);
    const offsetAmplitude = signal[offsetIndex];
    const minDifference = getDifference(minTimeValue, onsetTimeValue, {
      time: wave.offset,
      amplitude: offsetAmplitude,
    });
    const maxDifference = getDifference(maxTimeValue, onsetTimeValue, {
      time: wave.offset,
      amplitude: offsetAmplitude,
    });
    return minDifference.amplitude + maxDifference.amplitude;
  }

  return (
    getDifference(maxTimeValue, onsetTimeValue).amplitude +
    getDifference(minTimeValue, onsetTimeValue).amplitude
  );
};

// only used for CL_p_1 REA rules
export const computePositiveNetDeflection = (
  wave: Wave,
  signal: number[],
  frequency: number,
) => {
  if (!isWaveOfType(wave, 'P')) {
    throw new Error('should only be used with P wave');
  }
  const onsetIndex = ms2ind(wave.onset, frequency);
  const onsetAmplitude = signal[onsetIndex];
  const onsetTimeValue = { time: wave.onset, amplitude: onsetAmplitude };
  const [, maxTimeValue] = getWaveMinMaxTimeValues(wave, signal, frequency);

  const offsetIndex = ms2ind(wave.offset, frequency);
  const offsetAmplitude = signal[offsetIndex];
  const maxDifference = getDifference(maxTimeValue, onsetTimeValue, {
    time: wave.offset,
    amplitude: offsetAmplitude,
  });
  return maxDifference.amplitude;
};

export const getTimeAmplitudes = (
  times: number[],
  signal: number[],
  frequency: number,
): TimeAmplitude[] =>
  times
    .filter((time) => {
      const index = ms2ind(time, frequency);
      return 0 <= index && index < signal.length;
    })
    .map((time) => {
      const index = ms2ind(time, frequency);
      return { time: time, amplitude: signal[index] };
    });

export const getDifference = (
  point: TimeAmplitude,
  referenceOnset: TimeAmplitude,
  referenceOffset?: TimeAmplitude,
) => {
  if (referenceOffset) {
    const linearAmplitude = linearAmplitudePoint(
      point.time,
      referenceOnset,
      referenceOffset,
    );
    return {
      time: point.time - linearAmplitude.time,
      amplitude: point.amplitude - linearAmplitude.amplitude,
    };
  }
  return {
    time: point.time - referenceOnset.time,
    amplitude: point.amplitude - referenceOnset.amplitude,
  };
};

export const linearAmplitudePoint = (
  time: number,
  referenceOnset: TimeAmplitude,
  referenceOffset: TimeAmplitude,
): TimeAmplitude => {
  const difference = {
    time: referenceOffset.time - referenceOnset.time,
    amplitude: referenceOffset.amplitude - referenceOnset.amplitude,
  };

  const ratio = (time - referenceOnset.time) / difference.time;
  return {
    time,
    amplitude: referenceOnset.amplitude + ratio * difference.amplitude,
  };
};

export const ind2ms = (index: number, frequency = 250) =>
  Math.round((index * SECOND) / frequency);

export const ms2ind = (millis: number, frequency = 250) =>
  Math.round((millis * frequency) / SECOND);

export const computeAxes = (waves: Wave[], leads: Leads, frequency: number) => {
  const axes = waves
    .map((wave) => {
      const mliNetDeflection = computeNetDeflection(
        wave,
        leads['MLI'],
        frequency,
      );
      const avfNetDeflection = computeNetDeflection(
        wave,
        leads['AVF'],
        frequency,
      );

      const axis =
        (Math.atan2(2 * avfNetDeflection, Math.sqrt(3) * mliNetDeflection) *
          180) /
        Math.PI;
      return axis;
    })
    .filter((axis): axis is number => axis != null);
  return axes;
};
